import { normalizeKnowledgeFacts } from '../knowledgeItems/knowledgeFactNormalizer';
import { KNOWLEDGE_ITEM_TYPES } from '../knowledgeItems/knowledgeItemModel';
import { CARE_LENS, getCareLensKey } from '../../utils/careLens';
import { normalizeChapterProvenance } from './chapterProvenance';
import { resolveKnowledgeSourceAction } from './knowledgeSourceAction';
import { buildProfileSourcePresentation } from './profileSourcePresentation';
import {
  PROFILE_PLACEMENT_CHAPTERS,
  isExplicitlyPlacedInAnotherChapter,
  isProfileFactLifecycleVisible,
} from './profilePlacementMetadata';
import { isKnowledgeFactProjectedToChapter } from './profileKnowledgeProjection';
import { PROFILE_CHAPTER_TITLES } from '../../constants/profileChapterTitles';

export const SAFETY_DOMAIN_ID = 'DM_06';

const GROUPS = Object.freeze({
  supervision_awareness: 'Supervision and awareness',
  home_environment: 'Home and environment',
  wandering: 'Leaving, wandering, and getting lost',
  mobility_falls: 'Mobility and falls',
  food_swallowing_allergies: 'Food, swallowing, and allergies',
  medication_safety: 'Medication safety',
  personal_safety: 'Personal safety and boundaries',
  emergency_plans: 'Emergency plans',
});

const DEFAULT_GROUP_ORDER = Object.freeze(Object.keys(GROUPS));
const BASE_ELIGIBLE_TYPES = new Set([
  KNOWLEDGE_ITEM_TYPES.SAFETY,
  KNOWLEDGE_ITEM_TYPES.RISK,
  KNOWLEDGE_ITEM_TYPES.INSTRUCTION,
]);

const cleanText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();
const getFactValue = (fact = {}) => cleanText(fact.summary || fact.title);
const valueKey = (value = '') => cleanText(value).toLowerCase().replace(/[.]+$/, '');

const contentHash = (value = '') => {
  let hash = 2166136261;
  for (const character of valueKey(value)) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const isEpisodicObservation = (value = '') => (
  /\b(?:today|yesterday|last night|this morning|once|one time|one appointment|one meal|one dose|one episode|during (?:an |the )?illness|on (?:monday|tuesday|wednesday|thursday|friday|saturday|sunday))\b/i
    .test(value)
);

const classifyFact = (value = '') => {
  const text = cleanText(value).toLowerCase();
  if (/\b(?:wander|wandering|elope|elopement|getting lost|get lost|leave unnoticed|leave through|exterior doors?|unlocked doors?)\b/.test(text)) return 'wandering';
  if (/\b(?:chok|swallow|aspirat|allerg|anaphyl|bite[ -]size|small pieces|thickened liquids?)\b/.test(text)) return 'food_swallowing_allergies';
  if (/\b(?:medications?|medicines?|rescue medicine)\b/.test(text)
    && /\b(?:lock|locked|out of reach|store|storage|direct heat|refrigerat|secure)\b/.test(text)) return 'medication_safety';
  if (/\b(?:written|confirmed|documented) (?:seizure |safety |emergency |comfort )?plan\b|\bfollow (?:the )?.+ emergency plan\b|\bemergency plan (?:is|stored)\b/.test(text)) return 'emergency_plans';
  if (/\b(?:arm['’]?s reach|line[ -]of[ -]sight|supervis|do not leave (?:them )?unattended|not recognize traffic danger|traffic awareness|near open water)\b/.test(text)) return 'supervision_awareness';
  if (/\b(?:fall risk|prevent falls?|loose rugs?|walking paths?|pathways? clear|well lit|walker within reach|transfer safety|mobility precaution)\b/.test(text)) return 'mobility_falls';
  if (/\b(?:unfamiliar adults?|personal boundar|body safety|privacy boundar|consent|personal safety)\b/.test(text)) return 'personal_safety';
  if (/\b(?:keep|secure|lock|remove|avoid|must remain|equipment)\b/.test(text)
    && /\b(?:home|environment|door|window|stove|outlet|sharp|oxygen|fire|pathway|equipment)\b/.test(text)) return 'home_environment';
  return '';
};

const isEligibleKnowledgeFact = (fact = {}) => {
  if (!isProfileFactLifecycleVisible(fact)) return false;
  if (isExplicitlyPlacedInAnotherChapter(fact, PROFILE_PLACEMENT_CHAPTERS.SAFETY)) return false;
  if (isKnowledgeFactProjectedToChapter(fact, PROFILE_PLACEMENT_CHAPTERS.SAFETY, { includeFallback: false })) return true;
  if (fact.needsQuickCheck && fact.profileVisibilityOverride !== 'pin') return false;
  if (isEpisodicObservation(getFactValue(fact))) return false;
  const groupId = classifyFact(getFactValue(fact));
  if (!groupId) return false;
  if (BASE_ELIGIBLE_TYPES.has(fact.knowledgeType)) return true;
  if (fact.knowledgeType === KNOWLEDGE_ITEM_TYPES.PROFILE) {
    return fact.profileField === 'safetyConsiderations';
  }
  if (fact.knowledgeType === KNOWLEDGE_ITEM_TYPES.FEEDING) {
    return groupId === 'food_swallowing_allergies';
  }
  if (fact.knowledgeType === KNOWLEDGE_ITEM_TYPES.MEDICATION) {
    return groupId === 'medication_safety';
  }
  return false;
};

const normalizeLegacyValues = (value) => (
  (Array.isArray(value) ? value : [value])
    .map((item) => cleanText(
      typeof item === 'string' ? item : item?.label || item?.name || item?.title || item?.value
    ))
    .filter(Boolean)
);

const buildSyntheticFact = ({ value, source }) => ({
  id: `legacy-safety-${source}-${contentHash(value)}`,
  title: value,
  summary: value,
  knowledgeType: KNOWLEDGE_ITEM_TYPES.SAFETY,
  profileField: source === 'support' ? 'safetyConsiderations' : '',
  factStatus: 'current',
  profileVisibilityOverride: null,
  confidence: 'high',
  needsQuickCheck: false,
  sourceRefs: [],
  responseGuidance: null,
  resolverEligible: true,
  synthetic: true,
});

// Retire these adapters after legacy Profile fields have a reviewed Knowledge migration.
const buildLegacySafetyFacts = (child = {}) => [
  ...normalizeLegacyValues(child.supportProfile?.safetyConsiderations)
    .filter((value) => classifyFact(value))
    .map((value) => buildSyntheticFact({ value, source: 'support' })),
  ...normalizeLegacyValues(child.medicalProfile?.foodAllergies)
    .map((value) => `Confirmed allergy: ${value}`)
    .map((value) => buildSyntheticFact({ value, source: 'allergy' })),
];

const deduplicateFacts = (facts = []) => {
  const output = [];
  const indexByValue = new Map();
  facts.forEach((fact) => {
    const key = valueKey(getFactValue(fact));
    if (!key) return;
    const existingIndex = indexByValue.get(key);
    if (existingIndex === undefined) {
      indexByValue.set(key, output.length);
      output.push(fact);
    } else if (output[existingIndex].synthetic && !fact.synthetic) {
      output[existingIndex] = fact;
    }
  });
  return output;
};

const getGroupOrder = (lens) => {
  if (lens === CARE_LENS.AUTISM) {
    return ['wandering', 'supervision_awareness', 'home_environment', 'personal_safety', 'food_swallowing_allergies', 'mobility_falls', 'medication_safety', 'emergency_plans'];
  }
  if (lens === CARE_LENS.DEMENTIA) {
    return ['wandering', 'mobility_falls', 'medication_safety', 'home_environment', 'supervision_awareness', 'emergency_plans', 'food_swallowing_allergies', 'personal_safety'];
  }
  if (lens === CARE_LENS.HOSPICE) {
    return ['mobility_falls', 'medication_safety', 'home_environment', 'food_swallowing_allergies', 'emergency_plans', 'supervision_awareness', 'personal_safety', 'wandering'];
  }
  return DEFAULT_GROUP_ORDER;
};

const consequencePriority = (value = '', groupId = '') => {
  const text = valueKey(value);
  if (/\b(?:open water|traffic danger|chok|aspirat|anaphyl|peanut allergy|leave unnoticed)\b/.test(text)) return 1000;
  return {
    supervision_awareness: 900,
    wandering: 875,
    home_environment: 825,
    emergency_plans: 800,
    food_swallowing_allergies: 775,
    medication_safety: 750,
    mobility_falls: 700,
    personal_safety: 600,
  }[groupId] || 0;
};

const buildHandbookCopy = (value = '', groupId = '') => {
  const text = cleanText(value).replace(/[.]+$/, '');
  const explanations = {
    supervision_awareness: 'This is an established supervision or awareness need.',
    home_environment: 'This is an established environmental precaution.',
    wandering: 'This precaution helps prevent leaving or getting lost.',
    mobility_falls: 'This is an established mobility or fall precaution.',
    food_swallowing_allergies: 'This is a confirmed food, swallowing, or allergy precaution.',
    medication_safety: 'This is a confirmed medication-handling precaution.',
    personal_safety: 'This is an established personal-safety consideration.',
    emergency_plans: 'This refers to a confirmed emergency plan.',
  };
  return { headline: text, explanation: explanations[groupId] };
};

// DM_06: attach sourceAction only for real Knowledge facts that already
// passed isEligibleKnowledgeFact/classifyFact - a diagnosis-only or
// isolated-incident fact never reaches this because it was never admitted as
// an entry in the first place. Synthetic legacy facts (safetyConsiderations,
// foodAllergies with no real Knowledge doc) never get a sourceAction.
const resolveFactSourceAction = (fact) => {
  if (fact.synthetic) return undefined;
  return resolveKnowledgeSourceAction(fact);
};

const buildProvenance = (facts = []) => normalizeChapterProvenance(
  facts.filter((fact) => !fact.synthetic).flatMap((fact) => fact.sourceRefs || []),
  'safety-source'
);

export const buildSafetyDomain = ({
  child = {},
  knowledgeFacts = [],
  careContext = child.careContext || {},
  ageGroup = careContext?.ageGroup || '',
} = {}) => {
  const lens = getCareLensKey({
    selectedConditions: child.conditions || [],
    careContext: careContext || child.careContext || {},
  });
  const groupOrder = getGroupOrder(lens);
  const realFacts = normalizeKnowledgeFacts(knowledgeFacts)
    .filter(isEligibleKnowledgeFact)
    .map((fact) => ({ ...fact, synthetic: false }));
  const rankedFacts = deduplicateFacts([...buildLegacySafetyFacts(child), ...realFacts])
    .map((fact, index) => ({
      fact,
      groupId: classifyFact(getFactValue(fact)) || (
        fact.profileChapterId === PROFILE_PLACEMENT_CHAPTERS.SAFETY
          ? 'supervision_awareness'
          : ''
      ),
      index,
    }))
    .filter((item) => item.groupId)
    .sort((left, right) => (
      consequencePriority(getFactValue(right.fact), right.groupId)
      - consequencePriority(getFactValue(left.fact), left.groupId)
      || Number(right.fact.profileVisibilityOverride === 'pin') - Number(left.fact.profileVisibilityOverride === 'pin')
      || groupOrder.indexOf(left.groupId) - groupOrder.indexOf(right.groupId)
      || left.index - right.index
    ));

  const groups = groupOrder.map((groupId) => ({
    id: groupId,
    title: GROUPS[groupId],
    entries: rankedFacts.filter((item) => item.groupId === groupId).map(({ fact }) => ({
      id: `entry-${fact.id}`,
      knowledgeFactId: fact.id,
      ...buildHandbookCopy(getFactValue(fact), groupId),
      responseGuidance: fact.responseGuidance || undefined,
      provenance: buildProvenance([fact]),
      sourcePresentation: buildProfileSourcePresentation(fact),
      sourceAction: resolveFactSourceAction(fact),
    })),
  })).filter((group) => group.entries.length);

  return {
    id: 'safety',
    title: PROFILE_CHAPTER_TITLES.SAFETY,
    description: 'Precautions and actions to help keep the person safe.',
    caregiverQuestion: 'How do I keep this person safe?',
    briefing: {
      items: rankedFacts.slice(0, 3).map(({ fact }) => ({
        id: `briefing-${fact.id}`,
        text: getFactValue(fact).replace(/[.]+$/, ''),
        explainedByEntryIds: [`entry-${fact.id}`],
      })),
      provenance: buildProvenance(rankedFacts.map((item) => item.fact)),
    },
    handbook: { groups },
    emptyState: {
      title: 'How do I keep this person safe?',
      description: 'Add supervision needs, environmental precautions, known hazards, or confirmed emergency plans that caregivers should follow.',
    },
    metadata: {
      domainId: SAFETY_DOMAIN_ID,
      careFocus: lens,
      ageGroup: ageGroup || careContext?.ageGroup || 'unknown',
      contractVersion: '1',
    },
  };
};
